import { LoadingSpinner } from 'components';
import React, { FunctionComponent, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { Disclaimer } from 'scenes/common/components/Awards';
import { QuoteErrorModal } from 'scenes/common/components/Error';
import { FullPageWrapper } from 'scenes/common/components/FullPageWrapper';
import SaveQuoteModal from 'scenes/common/components/QuoteInformation/QuoteNumberCards/SaveQuoteModal';
import FlowRouter from 'scenes/common/flows/FlowRouter';
import {
  hasNonRecoverableBusinessErrors,
  isApiReloading,
  isErrorWhileSavingQuote,
  isLoadingWithNoData,
  isQuoteSaved,
} from 'scenes/common/helpers/apiData';
import { useAwards } from 'scenes/common/hooks/useAwards/useAwards';
import {
  isBeforeEffectiveEndDate,
  isUnderMaintenance,
} from 'scenes/common/selectors/configSelectors';
import { FunnelType, LocationState } from 'scenes/common/types';
import analytics from 'scenes/motor/analytics';
import {
  QuoteErrorPage,
  RecoverableQuoteErrorModal,
} from 'scenes/motor/components/Error';
import { ApiError } from 'scenes/motor/components/Error/components';
import withMotorFunnel from 'scenes/motor/components/HOC/withMotorFunnel';
import stepRoutes from 'scenes/motor/components/Steps/stepRoutes';
import motorPageTitles from 'scenes/motor/components/helpers/pageTitles';
import { IntersitialContextProvider } from 'scenes/motor/context';
import {
  getPolicyStartDate,
  getSaveQuoteEmailAddress,
} from 'scenes/motor/selectors';
import { MotorFlowType, MotorFullPageProps } from 'scenes/motor/types';
import { Footer } from '../Footer';
import { supportFootnote } from '../Footer/components';
import { Header } from '../Header';
import { MotorProgressBar } from '../ProgressBar';
import { SaveQuoteComplete } from '../SaveQuoteComplete';
import { Sidebar } from '../Sidebar';
import { showFooter, showProgressBar, showSidebar } from '../helpers';

const SupportingNote = () => supportFootnote;

const showSupportingNote = (pathname: string) =>
  pathname.includes('/motor/quote-price') ? [SupportingNote] : [];

const FormPage: FunctionComponent<MotorFullPageProps> = (props) => {
  const { productType } = useParams<{ productType: FunnelType }>();
  const {
    funnelState,
    onPrevious,
    onRestartQuote,
    onResumeQuote,
    onClearApiErrors,
    onErrorRecoveryDeclined,
    vehicleData,
    referenceData,
    onSaveQuote,
    showSaveQuoteModal,
    onClickSaveQuoteModal,
    onErrorRecovery,
    onCreateQuote,
  } = props;

  const location = useLocation<LocationState<MotorFlowType>>();
  useEffect(() => {
    if (
      !hasNonRecoverableBusinessErrors(funnelState.apiResponses.quoteDetails) &&
      !isQuoteSaved(funnelState.apiResponses.quoteDetails) &&
      !isLoadingWithNoData(funnelState.apiResponses.quoteDetails) &&
      !isUnderMaintenance(funnelState.apiResponses.config, FunnelType.Motor)
    ) {
      document.title = `${
        motorPageTitles[funnelState.step]
      } | Coles Car Insurance`;
    } else {
      document.title = 'Coles Car Insurance';
    }
  }, [
    funnelState.apiResponses.config,
    funnelState.apiResponses.quoteDetails,
    funnelState.step,
  ]);

  const reloadPage = () => window.location.reload();
  const quoteSaved = isQuoteSaved(funnelState.apiResponses.quoteDetails);
  const resumeQuote = () => {
    onClickSaveQuoteModal();
    onResumeQuote();
  };

  const { motorDisclaimersToDisplay } = useAwards();

  return (
    <FullPageWrapper
      onClickCallUs={() => analytics().callUs(funnelState)}
      isLoading={isApiReloading(funnelState.apiResponses.quoteDetails)}
      motorExperimentalStyles
      MotorProgressBar={
        showProgressBar(funnelState, location) ? (
          <MotorProgressBar funnelState={funnelState} />
        ) : undefined
      }
      Header={() => (
        <Header funnelState={funnelState} onPrevious={onPrevious} />
      )}
      Footer={
        showFooter(funnelState)
          ? () => <Footer funnelState={funnelState} />
          : undefined
      }
      Sidebar={
        showSidebar(funnelState)
          ? () => <Sidebar funnelState={funnelState} />
          : undefined
      }
      disclaimersToDisplay={
        !isLoadingWithNoData(funnelState.apiResponses.quoteDetails)
          ? [
              ...motorDisclaimersToDisplay(
                funnelState
              ).map((disclaimer) => () => (
                <Disclaimer
                  symbol={disclaimer.disclaimerSymbol}
                  content={disclaimer.disclaimer}
                />
              )),
              ...showSupportingNote(window.location.pathname),
            ]
          : undefined
      }
    >
      {hasNonRecoverableBusinessErrors(
        funnelState.apiResponses.quoteDetails
      ) && (
        <QuoteErrorPage
          funnelState={funnelState}
          onRestartQuote={onRestartQuote}
        />
      )}
      {isLoadingWithNoData(funnelState.apiResponses.quoteDetails) && (
        <LoadingSpinner label="Loading your quote" />
      )}
      {!isLoadingWithNoData(funnelState.apiResponses.quoteDetails) &&
        !hasNonRecoverableBusinessErrors(
          funnelState.apiResponses.quoteDetails
        ) &&
        !isUnderMaintenance(
          funnelState.apiResponses.config,
          FunnelType.Motor
        ) && (
          <>
            <IntersitialContextProvider>
              <FlowRouter
                stepProps={props}
                stepRoutes={stepRoutes}
                productType={productType}
              />
              <QuoteErrorModal
                onClear={reloadPage}
                apiResponse={[referenceData, vehicleData]}
                ImageComponent={ApiError}
              />
              <QuoteErrorModal
                onClear={onClearApiErrors}
                apiResponse={[funnelState.apiResponses.quoteDetails]}
                ImageComponent={ApiError}
              />
              <RecoverableQuoteErrorModal
                onDeclineAction={onErrorRecoveryDeclined}
                onRecoveryActions={onErrorRecovery}
                onCreateQuote={onCreateQuote}
                funnelState={funnelState}
              />
            </IntersitialContextProvider>
          </>
        )}
      {showSaveQuoteModal &&
        !isLoadingWithNoData(funnelState.apiResponses.quoteDetails) &&
        !hasNonRecoverableBusinessErrors(
          funnelState.apiResponses.quoteDetails
        ) &&
        !isUnderMaintenance(
          funnelState.apiResponses.config,
          FunnelType.Motor
        ) && (
          <SaveQuoteModal
            isOpen={
              showSaveQuoteModal &&
              !isErrorWhileSavingQuote(funnelState.apiResponses.quoteDetails)
            }
            onClose={quoteSaved ? resumeQuote : onClickSaveQuoteModal}
            onSaveQuote={onSaveQuote}
            isQuoteSaveInProgress={isApiReloading(
              funnelState.apiResponses.quoteDetails
            )}
            contactEmailAddress={getSaveQuoteEmailAddress(funnelState)}
            isQuoteSaved={quoteSaved}
            renderSaveQuoteComplete={() => (
              <SaveQuoteComplete
                funnelState={funnelState}
                onResumeQuote={resumeQuote}
              />
            )}
            isBeforeEffectiveEndDate={isBeforeEffectiveEndDate(
              funnelState.apiResponses.config,
              getPolicyStartDate(funnelState)
            )}
          />
        )}
    </FullPageWrapper>
  );
};

export default withMotorFunnel(FormPage);
